/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useState } from "react";
import download from "downloadjs";
import { Else, If, Then } from "react-if";
import { BrickLoader, Button, Modal, theme } from "@nordcloud/gnui";
import { ActionTypeProperties } from "~/generated/graphql";
import { LoaderWrap } from "~/components";
import { CodeHighlighter } from "~/components/CodeHighlighter";
import { ModalContent, ModalFooter } from "~/components/Styled";
import { showError } from "~/services/toast";
import { isEmpty, isNotEmpty } from "~/tools";
import { useGetScriptContent } from "~/views/customActions/hooks/useGetScriptContent/useGetScriptContent";

type Props = {
  isOpen: boolean;
  close: () => void;
  actionId?: string;
  typeProperties?: ActionTypeProperties | null;
};

const getScriptFilename = (typeProperties?: ActionTypeProperties | null) => {
  const s3FileUrl =
    typeProperties &&
    typeof typeProperties === "object" &&
    "s3FileUrl" in typeProperties
      ? typeProperties.s3FileUrl
      : undefined;
  if (!s3FileUrl) {
    return "script.txt";
  }

  return s3FileUrl.replace(/^.*[\\\/]/, "").split("?")[0] || "script.txt";
};

const getLanguageFromFilename = (filename: string) => {
  const extension = filename.includes(".")
    ? filename.slice(filename.lastIndexOf(".") + 1).toLowerCase()
    : undefined;

  switch (extension) {
    case "ps1":
      return "powershell";
    case "sh":
    case "bash":
      return "bash";
    default:
      return "text";
  }
};

export function ViewScriptModal({
  isOpen,
  close,
  actionId,
  typeProperties,
}: Props) {
  const scriptFilename = getScriptFilename(typeProperties);
  const scriptLanguage = getLanguageFromFilename(scriptFilename);
  const { scriptContent, loading, error } = useGetScriptContent({
    actionId,
    enabled: isOpen,
  });
  const [fetchError, setFetchError] = useState("");

  useEffect(() => {
    if (isOpen && !actionId) {
      setFetchError("Script file is not available.");
    }
    if (isOpen && actionId) {
      setFetchError("");
    }
  }, [actionId, isOpen]);

  useEffect(() => {
    if (!error) {
      return;
    }
    setFetchError(error.message || "Failed to load script.");
    showError(error.message || "Failed to load script.");
  }, [error]);

  const onClickDownload = () => {
    download(scriptContent, scriptFilename, "text/plain");
  };

  return (
    <Modal
      onClose={close}
      isOpen={isOpen}
      contentLabel="Script"
      contentMaxHeight="80vh"
      modalMinWidth={scriptContent?.length > 5000 ? "90vw" : "50rem"}
    >
      <ModalContent css={{ maxHeight: "70vh", overflowY: "auto" }}>
        <LoaderWrap loading={loading} Component={BrickLoader}>
          <If condition={isNotEmpty(fetchError)}>
            <Then>{fetchError}</Then>
            <Else>
              <CodeHighlighter language={scriptLanguage} code={scriptContent} />
            </Else>
          </If>
        </LoaderWrap>
      </ModalContent>
      <ModalFooter
        mt={theme.spacing.spacing02}
        justifyContent="flex-end"
        gap={theme.spacing.spacing03}
      >
        <Button
          icon="download"
          onClick={onClickDownload}
          severity="low"
          disabled={isEmpty(scriptContent)}
        >
          Download File
        </Button>
      </ModalFooter>
    </Modal>
  );
}
